<template>
  <div class="mx-0" v-if="report.length === 0 && loading === false">
    No data available
  </div>
  <graph-skeleton class="mx-0" :height="355" v-else-if="report.length === 0"></graph-skeleton>
  <div v-else id="reportChart" class="mx-0">
    <highcharts :options="graph" ref="highcharts"></highcharts>
    <graph-legend :stats="statColors"></graph-legend>
  </div>
</template>

<style scoped>
  #reportChart {
    overflow: hidden;
  }
</style>

<script>
  import { getTimeZoneOffsetMillis, graphColors, getPrettyStatName, formatStat, getFormattedDate } from '@/reports/components/ReportUtils'
  import GraphLegend from '@/reports/components/graphs/GraphLegend'
  import { getTooltipHTML, getStyledGraphOptions, getYAxisStyle } from '@/reports/components/graphs/ReportGraphUtils'
  import GraphSkeleton from '@/reports/components/graphs/GraphSkeleton'

  import moment from 'moment'
  window.moment = moment

  export default {
    data() {
      return {
        statColors: []
      }
    },
    props: {
      report: { required: true, type: Array },
      stats: { required: true, type: Array },
      fields: { required: true, type: Array },
      timezone: { required: false, type: String },
      jointTooltip: { required: false, type: Boolean, default: false }, //whether tooltip shows info for all stats or just the ones that are hovered
      interval: { required: false, type: String }, //used to calculated date format for tooltip
      secondAxisStats: { required: false, type: Array },
      loading: { type: Boolean, default: true }
    },
    watch: {
      statColors: {
        handler: function() {
          this.$nextTick(function() {
            for(let color of this.statColors) {
              let name = color.name
              if(color.disabled) {
                this.hideSeries(name)
              } else {
                this.showSeries(name)
              }
            }
          })
        },
        deep: true
      }
    },
    methods: {
      getNewStatColors() {
        this.statColors = []
        for(let x = 0; x < this.stats.length; x++) {
          let obj = {}
          obj.color = graphColors[x]
          obj.name = this.stats[x]
          obj.disabled = false
          this.statColors.push(obj)
        }
      },
      //convert report into the format we need
      getSingleSeries() {
        this.getNewStatColors()
        let series = []
        for(let stat of this.stats) {
          let entry = { data: [] }
          entry.name = stat
          if (this.secondAxisStats && this.secondAxisStats.includes(stat)){
            entry.yAxis = 1
          }
          for(let row of this.report) {
            let datenum = Number(row.date)
            entry.data.push([ datenum, Number(row[stat]) ])
          }
          series.push(entry)
        }
        return series
      },
      //get all charts that are truthy
      getValidCharts() {
        let list = this.Highcharts.charts.filter( chart => chart )
        return list
      },
      getVisibleSeries(chart) {
        return chart.series.filter( series => series.visible )
      },
      hideSeries(name) {
        for (let chart of this.getValidCharts()) {
          let seriesList = chart.series.filter( series => series.name === name )
          for(let series of seriesList) {
            if(series.visible){
              series.hide()
            }
          }
        }
      },
      showSeries(name) {
        for (let chart of this.getValidCharts()) {
          let seriesList = chart.series.filter( series => series.name === name )
          for(let series of seriesList) {
            if(!series.visible){
              series.show()
            }
          }
        }
      },
      getFormattedTooltipDate(date) {
        return getFormattedDate(date, this.interval, this.timezone)
      },
      getTimeZoneOffsetMillis
    },
    computed: {
      /* this method does two different things, depending on the fields
      if there's one field (ie, the only thing being selected by is date), it'll make a series for a graph where each line is a stat
      if there are two fields, it'll make multiple series, one series for each stat, where each line is one of whatever else is being selected for
      if there are more than two fields, it won't return anything cuz that behavior is undefined
      */
      computedSeries () {
        return this.getSingleSeries()
      },
      hasSecondAxis () {
        return Boolean(this.secondAxisStats)
      },
      secondAxisTitle() {
        if(this.secondAxisStats) {
          let options = []
          for(let option of this.secondAxisStats) {
            options.push(getPrettyStatName(option))
          }
          let title = options.join(' ,')
          return title
        }
      },
      graph() {
        let self = this
        let options = {
          chart: {
            zoomType: 'x',
            backgroundColor: 'white',
            type: 'spline',
            height: 300,
            style: {
              fontFamily: 'Gilroy, Helvetica, sans-serif'
            },
            marginRight: (this.hasSecondAxis ? 50 : null)
          },
          plotOptions: {
            spline: {
              marker: {
                enabled: true,
                lineColor: null,
                symbol: 'circle',
                radius: 3
              },
              states: {
                hover: {
                  halo: false
                }
              }
            },
          },
          colors: graphColors,
          title: {
            text: '',
          },
          xAxis: {
            type: 'datetime',
            dateTimeLabelFormats: {
              millisecond: '%H:%M:%S.%L',
              second: '%H:%M:%S',
              minute: '%H:%M',
              hour: '%H:%M',
              day: '%b %e',
              week: '%b %e',
              month: '%b \'%y',
              year: '%Y'
            }
          },
          yAxis: [
            _.merge({
              title: '',
              opposite: this.hasSecondAxis
            }, getYAxisStyle()),
            _.merge({
              title: {
                text: this.secondAxisTitle
              },
              showEmpty: false,
              opposite: !this.hasSecondAxis
            }, getYAxisStyle()),
          ],
          legend: {
            enabled: false
          },
          series: this.computedSeries,
          time: {
            timezone: this.timezone
          },
          tooltip: {
            useHTML: true,
            borderColor: '#1C1D29',
            backgroundColor: '#1C1D29',
            borderRadius: 5,
            followPointer: false,
            shared: this.jointTooltip,
            formatter: function () {
              let title
              if(self.interval) {
                //make sure it works with all series hidden
                title = self.getFormattedTooltipDate(this.x)
              } else {
                title = "Today's Statistics"
              }
              if(self.jointTooltip) {
                let points = []
                for(let point of this.points) {
                  points.push({ name: getPrettyStatName(point.series.name), color: point.color, value: formatStat(point.series.name, point.y)})
                }
                return getTooltipHTML(title, points)
              } else {
                return getTooltipHTML(title, [{ name: getPrettyStatName(this.point.series.name), color: this.point.color, value: formatStat(this.point.series.name, this.point.y)}])
              }
            }
          }
        }
        return _.merge(options, getStyledGraphOptions())
      }
    },
    components: {
      GraphLegend,
      GraphSkeleton
    }
  }
</script>



// WEBPACK FOOTER //
// src/reports/components/graphs/ReportTimeGraph.vue